/* eslint-disable react-hooks/exhaustive-deps */
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useHTTPError } from 'br2s-ui-common-utils-02';
import useLoadCaptcha from '@/hooks/useLoadCaptcha';
import {
  Button,
  InlineAlert,
  TextField,
  TextFieldInput,
} from 'br2s-ui-componentlibrary_r2_02';
import { EMAIL_ERROR_MESSAGE } from '../crmConstants';
import { CRM_FORM } from '@/services/api/crm/crm';
import { generateSession, modifyQueryParam } from '../crmUtilities/crmUtils';

import '../crm-form.scss';

type Alert = {
  type: 'error' | 'success' | 'informative' | 'warning';
  message: ReactNode;
};

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const CRMSurveyUnsubscriptionForm = () => {
  useLoadCaptcha();
  const { handleHTTPError } = useHTTPError();

  const isFirstRender = useRef(true);
  const [email, setEmail] = useState('');
  const [emailConfirmation, setEmailConfirmation] = useState('');
  const [emailConfirmationError, setEmailConfirmationError] = useState('');
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const [alert, setAlert] = useState<Alert>({
    type: 'informative',
    message: '',
  });
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);

  useEffect(() => {
    if (isFirstRender.current) {
      fetchSession();     
      isFirstRender.current = false;
      return () => window.sessionStorage.removeItem('transactionId');
    }
  }, []);

  // istanbul ignore next
  const fetchSession = async () => {
    try {
      await generateSession();
      formLoad();
    } catch (error) {
      console.error(error);
      handleHTTPError(error, {
        errorType: 'inline',
      });
    }
  };

  const formLoad = async () => {
    window.sessionStorage.setItem('transactionId', uuidv4());
    const encryptedFormParam = modifyQueryParam();
    try {
      const response = await CRM_FORM.getSurveyUnsubscribe(encryptedFormParam);
      if (response.status === 200) {
        const details = response.data.result;
        setEmail(details?.customerEmail || '');
        setIsSubmitDisabled(false);
      }
    } catch (error: any) {
      console.log(error);
      handleHTTPError(error, {
        errorType: 'inline',
      });
      setIsSubmitDisabled(false);
    }
  };

  const getEmailConfirmationInputState = () => {
    if (emailConfirmationError !== undefined && emailConfirmationError !== '') {
      return 'error';
    }
    if (isSubmitClicked) {
      return 'success';
    }
    return 'default';
  };

  // set the empty error to alert.
  const setFormEmptyError = () => {
    if (!alert.message) {
      setAlert({
        type: 'error',
        message: 'Please fill in the required fields',
      });
    }
  };

  const validateEmailConfirmation = (val: string) => {
    let isValid = true;
    if (val === '') {
      setEmailConfirmationError('required');
      setFormEmptyError();
      isValid = false;
    } else if (val.toLowerCase() !== email.toLowerCase()) {
      setEmailConfirmationError('missmatched');
      isValid = false;
    } else {
      setEmailConfirmationError('');
    }
    if (isValid) {
      if (alert.message) {
        setAlert({
          type: 'informative',
          message: '',
        });
      }
    }
    return isValid;
  };

  const resetUnsubscriptionForm = () => {
    setEmailConfirmation('');
    setEmailConfirmationError('');
    setIsSubmitClicked(false);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
    window.sessionStorage.setItem('transactionId', uuidv4());
  };

  const handleSubmit = async () => {
    setIsSubmitClicked(true);

    const isValid = validateEmailConfirmation(emailConfirmation);
    if (!isValid) {
      return;
    }

    setIsSubmitDisabled(true);

    // @ts-ignore
    window.grecaptcha.ready(() => {
      // @ts-ignore
      window.grecaptcha
        .execute(RECAPTCHA_SITE_KEY, { action: 'submit' })
        .then(async (captchaToken: string) => {
          try {
            const payload = {
              requestTransactId:
                window.sessionStorage.getItem('transactionId') || '',
              webformParameter: modifyQueryParam(),
              customerEmail: emailConfirmation,
            };

            const response = await CRM_FORM.submitSurveyUnsubscription(
              payload,
              captchaToken
            );

            if (response.status === 200) {
              setAlert({
                type: 'success',
                message: 'Success: You have been unsubscribed from our survey.',
              });
              resetUnsubscriptionForm();
            }
          } catch (error: any) {
            console.error(error);
            handleHTTPError(error, {
              errorType: 'inline',
            });
            setIsSubmitDisabled(false);
          }
        });
    });
  };

  return (
    <div className="crm-form-wrapper">
      <div className="container crm-form">
        {alert.message && (
          <InlineAlert
            type={alert.type}
            // @ts-ignore
            headline={alert.message}
          />
        )}
        <TextField id="email" label="Email Address" state="disabled">
          <TextFieldInput id="email" name="email" value={email} isDisabled />
        </TextField>
        <TextField
          id="email-confirmation"
          label="Confirm Email Address"
          state={getEmailConfirmationInputState()}
        >
          <TextFieldInput
            id="email-confirmation"
            name="email-confirmation"
            placeholder="Enter valid email address"
            value={emailConfirmation}
            onChange={(e) => {
              setEmailConfirmation(e.target.value);
              validateEmailConfirmation(e.target.value);
            }}
            helperText={EMAIL_ERROR_MESSAGE[emailConfirmationError]}
          />
        </TextField>
        <Button
          id="submit"
          label="Submit"
          onClick={handleSubmit}
          isBlock
          isDisabled={isSubmitDisabled}
        />
      </div>
    </div>
  );
};

export default CRMSurveyUnsubscriptionForm;
